import { UNITS } from '@/types/units'
import { BUILDINGS } from '@/types/constants'

/**
 * Calculate the current population from existing troops, supporting troops, and troops in queue
 * Replicates the Rust logic: current_population()
 * 
 * NOTE: Building queue population is NOT counted here.
 * This matches the backend behavior where current_population() counts:
 * - troops (owned units in this village)
 * - supporting_troops (units from other villages defending this village)
 * - troops_queue (units currently being trained)
 * - deployed_population (units in-transit for attacks/support)
 * Building upgrades only check if there's enough capacity at the time of upgrade,
 * but they don't "reserve" population slots while in the queue.
 */
export function calculateCurrentPopulation(village: any): number {
	if (!village) return 0

	let pop = 0

	// Count existing troops
	if (village.troops) {
		for (const unit of village.troops) {
			if (unit.count > 0) {
				const unitKey = Object.keys(unit.troopType)[0] as keyof typeof UNITS
				const unitData = UNITS[unitKey]
				if (unitData) {
					pop += unit.count * unitData.population_cost
				}
			}
		}
	}

	// Count supporting troops (troops from other villages defending this village)
	if (village.supportingTroops) {
		for (const unit of village.supportingTroops) {
			if (unit.count > 0) {
				const unitKey = Object.keys(unit.troopType)[0] as keyof typeof UNITS
				const unitData = UNITS[unitKey]
				if (unitData) {
					pop += unit.count * unitData.population_cost
				}
			}
		}
	}

	// Count troops in queue
	if (village.troopsQueue) {
		for (const purchase of village.troopsQueue) {
			if (purchase.amount > 0) {
				const unitKey = Object.keys(purchase.troopType)[0] as keyof typeof UNITS
				const unitData = UNITS[unitKey]
				if (unitData) {
					pop += purchase.amount * unitData.population_cost
				}
			}
		}
	}

	// Count troops deployed (in-transit attacks/support)
	if (village.deployedPopulation) {
		pop += village.deployedPopulation
	}

	return pop
}

/**
 * Calculate the farm capacity based on farm level
 * Replicates the Rust logic: farm_capacity()
 * Formula: 240 * 1.17^farmLevel
 */
export function calculateFarmCapacity(village: any): number {
	if (!village || !village.buildings) return 240

	const farmLevel = village.buildings[BUILDINGS.farm]?.level || 0
	return Math.floor(240 * Math.pow(1.17, farmLevel))
}

/**
 * Get the available population slots
 */
export function getAvailablePopulation(village: any): number {
	const capacity = calculateFarmCapacity(village)
	const current = calculateCurrentPopulation(village)
	return Math.max(0, capacity - current)
}

/**
 * Check if there's enough population for additional troops
 */
export function hasEnoughPopulation(village: any, additionalPopulation: number): boolean {
	const available = getAvailablePopulation(village)
	return available >= additionalPopulation
}

/**
 * Get population usage percentage (0-100)
 */
export function getPopulationPercentage(village: any): number {
	const capacity = calculateFarmCapacity(village)
	if (capacity === 0) return 0

	const current = calculateCurrentPopulation(village)
	return Math.min(100, Math.round((current / capacity) * 100))
}

/**
 * Calculate the population capacity increase when upgrading farm to next level
 */
export function calculateFarmCapacityIncrease(currentLevel: number): number {
	const currentCapacity = Math.floor(240 * Math.pow(1.17, currentLevel))
	const nextCapacity = Math.floor(240 * Math.pow(1.17, currentLevel + 1))
	return nextCapacity - currentCapacity
}
